import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * LinkedIn OAuth Debug Page
 *
 * This page is used to debug the LinkedIn OAuth flow.
 * It shows all the state that would be available during the callback
 * and allows manual testing of the community context retrieval.
 *
 * Access at: /auth/linkedin-debug
 */
export default function LinkedInDebug() {
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const gatherDebugInfo = async () => {
      console.log('=== LinkedIn Debug Page ===');

      const info: any = {
        timestamp: new Date().toISOString(),
        location: {
          href: window.location.href,
          origin: window.location.origin,
          hostname: window.location.hostname,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        },
        referrer: document.referrer || '(none)',
        urlParams: Object.fromEntries(
          new URLSearchParams(window.location.search)
        ),
        localStorage: {
          linkedin_oauth_session: localStorage.getItem(
            'linkedin_oauth_session'
          ),
          linkedin_oauth_community: localStorage.getItem(
            'linkedin_oauth_community'
          ),
        },
      };

      // Log a distinctive marker
      console.log('🟢 LinkedInDebug: Page loaded at', info.timestamp);
      console.log('🟢 LinkedInDebug: Referrer was:', info.referrer);

      // Check session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      info.session = {
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email,
        provider: session?.user?.app_metadata?.provider,
        error: sessionError?.message,
      };

      // Check for LinkedIn identity
      if (session?.user) {
        const linkedInIdentity = session.user.identities?.find(
          (i: any) => i.provider === 'linkedin_oidc'
        );
        info.linkedInIdentity = {
          hasIdentity: !!linkedInIdentity,
          sub: linkedInIdentity?.identity_data?.sub,
          email: linkedInIdentity?.identity_data?.email,
          name: linkedInIdentity?.identity_data?.name,
        };

        // Check profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, email, role, first_name, last_name')
          .eq('id', session.user.id)
          .single();

        info.profile = {
          hasProfile: !!profile,
          ...profile,
          error: profileError?.message,
        };

        // Check community memberships
        // Note: community_members uses composite PK (profile_id, community_id), not 'id'
        const { data: memberships, error: memberError } = await supabase
          .from('community_members')
          .select(
            'profile_id, community_id, status, onboarding_completed, communities(slug, name)'
          )
          .eq('profile_id', session.user.id);

        info.memberships = {
          count: memberships?.length || 0,
          memberships,
          error: memberError?.message,
        };
      }

      // Check pending OAuth contexts
      const { data: pendingContexts, error: contextError } = await supabase
        .from('pending_oauth_context')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      info.pendingContexts = {
        count: pendingContexts?.length || 0,
        contexts: pendingContexts,
        error: contextError?.message,
      };

      // Log everything
      console.log('Debug Info:', JSON.stringify(info, null, 2));

      setDebugInfo(info);
      setLoading(false);
    };

    gatherDebugInfo();
  }, []);

  const handleProcessLinkedIn = async () => {
    const sessionToken = localStorage.getItem('linkedin_oauth_session');

    // Try database lookup
    if (sessionToken) {
      const { data: context, error } = await supabase
        .from('pending_oauth_context')
        .select('community_slug, session_token')
        .eq('session_token', sessionToken)
        .single();

      alert(
        `Session Token Lookup:\n${JSON.stringify({ context, error }, null, 2)}`
      );
    }

    // Try fallback lookup
    const { data: recentContext, error: recentError } = await supabase
      .from('pending_oauth_context')
      .select('community_slug, session_token')
      .eq('provider', 'linkedin_oidc')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    alert(
      `Fallback Lookup:\n${JSON.stringify({ recentContext, recentError }, null, 2)}`
    );
  };

  const handleCleanup = async () => {
    // Clean up all pending contexts
    const { error } = await supabase
      .from('pending_oauth_context')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000');

    if (error) {
      alert(`Error cleaning up: ${error.message}`);
    } else {
      alert('Cleaned up pending contexts!');
      window.location.reload();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8 bg-gray-100">
        <h1 className="text-2xl font-bold mb-4">LinkedIn OAuth Debug</h1>
        <p>Loading debug info...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 bg-gray-100">
      <h1 className="text-2xl font-bold mb-4">LinkedIn OAuth Debug</h1>

      <div className="space-y-4">
        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">Actions</h2>
          <div className="space-x-2">
            <button
              onClick={handleProcessLinkedIn}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Test Context Lookup
            </button>
            <button
              onClick={handleCleanup}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Clean Up Contexts
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded shadow border-2 border-yellow-400">
          <h2 className="font-semibold mb-2">Referrer (where you came from)</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {debugInfo.referrer || '(none)'}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">Location</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.location, null, 2)}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">URL Params</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.urlParams, null, 2)}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">LocalStorage</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.localStorage, null, 2)}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">Session</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.session, null, 2)}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">LinkedIn Identity</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.linkedInIdentity, null, 2)}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">Profile</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.profile, null, 2)}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">Community Memberships</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.memberships, null, 2)}
          </pre>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold mb-2">Pending OAuth Contexts</h2>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(debugInfo.pendingContexts, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}
